import React, { useMemo, useState } from 'react';

import useCurrencies from '../hooks/useCurrencies';
import useMetals from '../hooks/useMetals';

import 'currency-flags/dist/currency-flags.css';

const managedMetals = ['XAU', 'XAG', 'XPT', 'XPD'];
const initialCurrencyCount = 30;

function CurrencyFlag({ code }) {
  if (code === 'BTC') {
    return <span className="currencies-matrix-fallback-flag">BTC</span>;
  }

  if (code === 'XAG') {
    return <span className="currencies-matrix-fallback-flag">Ag</span>;
  }

  return <span className={`currency-flag currency-flag-${code.toLowerCase()}`} />;
}

function CurrenciesMatrix() {
  const currencies = useCurrencies();
  const metals = useMetals();
  const [showAll, setShowAll] = useState(false);

  const orderedCurrencies = useMemo(() => {
    if (!currencies) return [];

    return [...currencies].sort((a, b) => a.cc.localeCompare(b.cc));
  }, [currencies]);

  const orderedMetals = useMemo(() => {
    if (!metals) return [];

    return managedMetals
      .map(symbol => metals.find(metal => metal.symbol === symbol))
      .filter(Boolean);
  }, [metals]);

  const visibleCurrencies = showAll ? orderedCurrencies : orderedCurrencies.slice(0, initialCurrencyCount);

  if (!currencies || !metals) {
    return <div className="currencies-matrix-loading">Loading supported pairs...</div>;
  }

  return (
    <div className="currencies-matrix-wrap">
      <div className="currencies-matrix-scroll">
        <table className="currencies-matrix-table">
          <thead>
            <tr>
              <th>Currency</th>
              {orderedMetals.map(metal => (
                <th key={metal.symbol}>
                  <span className="currencies-matrix-metal-head">
                    <img src={metal.image} alt={`${metal.title} icon`} />
                    <span>{metal.symbol}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleCurrencies.map(currency => (
              <tr key={currency.cc}>
                <td>
                  <span className="currencies-matrix-currency">
                    <CurrencyFlag code={currency.cc} />
                    <span>
                      <b>{currency.cc}</b>
                      <small>{currency.name}</small>
                    </span>
                  </span>
                </td>
                {orderedMetals.map(metal => {
                  const isSupported = metal.currencies.some(item => item.curr === currency.cc);

                  return (
                    <td key={`${metal.symbol}-${currency.cc}`}>
                      {isSupported ? (
                        <span className="currencies-matrix-supported">
                          <span className="currencies-matrix-check">✓</span>
                          <span>{metal.symbol}{currency.cc}</span>
                        </span>
                      ) : (
                        <span className="currencies-matrix-missing">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {orderedCurrencies.length > initialCurrencyCount && (
        <div className="currencies-matrix-actions">
          <button type="button" onClick={() => setShowAll(value => !value)}>
            {showAll ? 'Show Less' : `Show All ${orderedCurrencies.length} Currencies`}
          </button>
        </div>
      )}
    </div>
  );
}

export default CurrenciesMatrix;
